import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { authHeaders, type WarehousePlan, type ZoneRow } from "../../../../api/rest";
import { decodeThemedPlan } from "../../../planDecode";
import { PLAN_MAX_EDGE_PX } from "../../../planRaster";
import {
  type Point, type View, boundsOf, fitView, panView, screenToWorld, unionBounds, worldToScreen, zoomViewAt,
} from "../../../mapCalibration";
import { ZONE_KIND_COLOR } from "../../../../scene/ZonesLayer";
import { centroid } from "./zoneGeometry";
import type { Action, EditorState } from "./zoneEditorState";
import { PLAN_SMOOTHING, drawArrow, drawHandles, drawRing, labelVisible } from "./zoneCanvasDraw";
import css from "./ZoneCanvas.module.css";

export interface ZoneCanvasProps {
  state: EditorState;
  dispatch: (a: Action) => void;
  footprints: number[][][];
  derived: ZoneRow[];
  plan: WarehousePlan | null;
  editable: boolean;
  fitKey: string;
  onCursor?: (world: [number, number] | null) => void;
}

const HIT_PX = 8;

interface PlanRaster { url: string; image: HTMLCanvasElement; width: number; height: number; resolution: number }

/**
 * ADR 403 — the zone drawing surface. Same Canvas 2D + callback-ref + ResizeObserver
 * shape as MapAlignTab (ADR 258/323): the canvas sits behind a loading branch, so a
 * plain ref effect would measure nothing; the view is STATE so drags do not re-fit it.
 * World y grows down the screen (ADR 311) — the editor reads like /live from above.
 */
export function ZoneCanvas({ state, dispatch, footprints, derived, plan, editable, fitKey, onCursor }: ZoneCanvasProps) {
  const { t } = useTranslation();
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const canvasRef = useCallback((node: HTMLCanvasElement | null) => {
    setCanvasEl(node);
    const parent = node?.parentElement;
    if (parent) {
      const r = parent.getBoundingClientRect();
      setSize({ width: Math.floor(r.width), height: Math.floor(r.height) });
    }
  }, []);

  useEffect(() => {
    const parent = canvasEl?.parentElement;
    if (!parent || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(parent);
    return () => ro.disconnect();
  }, [canvasEl]);

  const worldBounds = useMemo(() => unionBounds(
    boundsOf(footprints as unknown as Point[][]),
    boundsOf([...state.working.map((z) => z.polygon), ...derived.map((z) => z.polygon ?? [])] as unknown as Point[][]),
  ), [footprints, state.working, derived]);

  const fitted = useMemo(() => fitView(worldBounds, size.width, size.height), [worldBounds, size.width, size.height]);
  const [view, setView] = useState<View | null>(null);
  const [fittedFor, setFittedFor] = useState<string | null>(null);
  // Adjust-during-render, guarded (ADR 136). Refit when the warehouse, the measured box or the
  // static geometry changes (late data, settled layout) - never on working.length, which would
  // discard the user's zoom on every add/delete.
  const fitSig = `${fitKey}|${size.width}x${size.height}|${footprints.length}|${derived.length}`;
  if (size.width > 0 && (view === null || fittedFor !== fitSig)) {
    setView(fitted); setFittedFor(fitSig);
  }
  const v = view ?? fitted;

  // Plan raster, fetched and decoded once per url exactly as the /live Minimap does
  // (ADR 204/215): /v1/models/** needs the bearer token an <img src> cannot carry.
  // State is only set from the async callback; a failed fetch/decode draws without
  // the backdrop, and a stale raster (url changed / plan removed) is filtered at read time.
  const [loaded, setLoaded] = useState<PlanRaster | null>(null);
  const planUrl = plan?.url ?? null;
  const planResolution = plan?.resolution ?? 0;
  useEffect(() => {
    if (!planUrl) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(planUrl, { headers: authHeaders(), credentials: "include" });
        if (!res.ok) return;
        const decoded = await decodeThemedPlan(await res.blob(), PLAN_MAX_EDGE_PX);
        if (!decoded || cancelled) return;
        const image = document.createElement("canvas");
        image.width = decoded.width; image.height = decoded.height;
        image.getContext("2d")?.putImageData(decoded.image, 0, 0);
        // The stored resolution is m per ORIGINAL pixel; the decode may downscale.
        setLoaded({ url: planUrl, image, width: decoded.width, height: decoded.height,
          resolution: planResolution * (decoded.sourceWidth / decoded.width) });
      } catch { /* no backdrop */ }
    })();
    return () => { cancelled = true; };
  }, [planUrl, planResolution]);
  const planRaster = loaded && loaded.url === planUrl ? loaded : null;

  useEffect(() => {
    const el = canvasEl;
    if (!el || size.width === 0) return;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = size.width * dpr; el.height = size.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);
    const style = getComputedStyle(el);
    const tok = (name: string, fb: string) => style.getPropertyValue(name).trim() || fb;
    const inkFaint = tok("--op-ink-500", "gray");
    const ink = tok("--op-ink-700", "black");
    const accent = tok("--op-blue-500", "blue");

    if (planRaster && plan) {
      const [px, py] = worldToScreen(v, plan.originX, plan.originY);
      ctx.imageSmoothingEnabled = PLAN_SMOOTHING;
      ctx.globalAlpha = 0.6;
      const m = planRaster.resolution * v.ppm;
      ctx.drawImage(planRaster.image, px, py, planRaster.width * m, planRaster.height * m);
      ctx.globalAlpha = 1;
    }
    for (const f of footprints) drawRing(ctx, v, f, inkFaint, null, 1);
    for (const d of derived) if (d.polygon) drawRing(ctx, v, d.polygon, ZONE_KIND_COLOR.aisle, ZONE_KIND_COLOR.aisle, 1, 0.18);

    ctx.font = "12px 'JetBrains Mono', monospace";
    for (const z of state.working) {
      const color = ZONE_KIND_COLOR[z.kind] ?? accent;
      const selected = z.id === state.selectedId;
      drawRing(ctx, v, z.polygon, color, color, selected ? 2.5 : 1.5, 0.25);
      if (z.kind === "one_way" && z.directionRad != null) drawArrow(ctx, v, centroid(z.polygon), z.directionRad, color);
      if (labelVisible(v, z.polygon)) {
        const [cx, cy] = worldToScreen(v, ...centroid(z.polygon));
        ctx.fillStyle = ink; ctx.fillText(z.id, cx, cy);
      }
      if (selected && editable) drawHandles(ctx, v, z.polygon, color);
    }
    if (state.pending) {
      drawRing(ctx, v, state.pending, accent, null, 1.5);
      drawHandles(ctx, v, state.pending, accent, 5);
    }
    if (state.drag?.type === "rect") {
      const { start, current } = state.drag;
      const [ax, ay] = worldToScreen(v, start[0], start[1]);
      const [bx, by] = worldToScreen(v, current[0], current[1]);
      ctx.save(); ctx.strokeStyle = accent; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]);
      ctx.strokeRect(Math.min(ax, bx), Math.min(ay, by), Math.abs(bx - ax), Math.abs(by - ay));
      ctx.restore();
    }
  }, [canvasEl, size, v, footprints, derived, plan, planRaster, state.working, state.selectedId, state.pending, state.drag, editable]);

  // `fitted` changes with every edit (the bounds include the working zones); the wheel
  // listener reads it through a ref so it registers once per canvas, not once per edit.
  const fittedRef = useRef(fitted);
  useEffect(() => { fittedRef.current = fitted; }, [fitted]);
  useEffect(() => {
    const el = canvasEl;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      setView((cur) => zoomViewAt(cur ?? fittedRef.current, Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [canvasEl]);

  const panRef = useRef<{ screen: Point } | null>(null);
  const local = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const s = local(e);
    if (editable && e.button === 2) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (e.button === 1 || e.shiftKey || !editable) { panRef.current = { screen: s }; return; }
    const w = screenToWorld(v, s[0], s[1]) as [number, number];
    const tolM = HIT_PX / v.ppm;
    dispatch(e.altKey ? { type: "altClick", p: w, tolM } : { type: "pointerDown", p: w, tolM });
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const s = local(e);
    const w = screenToWorld(v, s[0], s[1]) as [number, number];
    onCursor?.(w);
    const pan = panRef.current;
    if (pan) {
      const dx = s[0] - pan.screen[0], dy = s[1] - pan.screen[1];
      setView((cur) => panView(cur ?? fitted, dx, dy));
      panRef.current = { screen: s };
      return;
    }
    if (editable && state.drag) dispatch({ type: "pointerMove", p: w });
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (panRef.current) { panRef.current = null; return; }
    if (editable || state.drag) dispatch({ type: "pointerUp" });
  };
  const onPointerCancel = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    panRef.current = null;
    if (editable || state.drag) dispatch({ type: "cancel" });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (!editable) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); dispatch({ type: "deleteSelected" }); }
    else if (e.key === "Escape") dispatch({ type: "cancel" });
    else if (e.key === "Enter") dispatch({ type: "closePolygon" });
  };

  return (
    <div className={css.canvasWrap} data-testid="zone-canvas-wrap">
      <canvas
        ref={canvasRef}
        className={css.canvas}
        style={{ width: size.width, height: size.height }}
        tabIndex={0}
        role="img"
        aria-label={t("structure.zones.canvas.aria", { defaultValue: "Editor de zonas" })}
        data-tool={state.tool}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={() => onCursor?.(null)}
        onKeyDown={onKeyDown}
        onContextMenu={(e) => e.preventDefault()}
      />
      <button type="button" className={css.fitBtn} onClick={() => setView(fitted)}>
        {t("structure.zones.tool.fit", { defaultValue: "Ajustar à tela" })}
      </button>
    </div>
  );
}
